import { useState, useMemo, useEffect } from "react";
import { Plus, MapPin, Pencil, Trash2, X, Search, Zap, Layers3, FolderOpen, Grid3x3, Spline, Split, Download, Upload, Loader2, Camera, Rows3, Server, Boxes, FileText, Unlink, Waypoints } from "lucide-react";
import { toast } from "sonner";
import api, { API, dlToken } from "@/lib/api";
import { MODULES } from "@/lib/modules";
import { cableOptical } from "@/lib/cableOptical";
import RecordForm from "@/components/RecordForm";
import CameraWall from "@/components/CameraWall";
import { FolderBar, FolderSelect } from "@/components/FolderBar";
import { useI18n } from "@/context/I18nContext";
import { useSettings } from "@/context/SettingsContext";

function _impVal(it, names) {
  const p = it.properties || {};
  for (const n of names) {
    for (const k of Object.keys(p)) {
      if (String(k).toLowerCase() === n && p[k] != null && p[k] !== "") return p[k];
    }
    if (it[n] != null && it[n] !== "") return it[n];
  }
  return null;
}

function CoverageBulkDialog({ onClose, onDone, t }) {
  const { settings } = useSettings();
  const [locs, setLocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState("");
  const [streets, setStreets] = useState([]);
  const [street, setStreet] = useState("");
  const [numFrom, setNumFrom] = useState("");
  const [numTo, setNumTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // null | {total, addresses}
  const [excluded, setExcluded] = useState(() => new Set());
  const [shared, setShared] = useState({
    medium: "", technologia: "", downlink_mbps: "", uplink_mbps: "",
    rodzaj_zasiegu: "rzeczywisty", infrastruktura_wlasna: "tak", przedstawiciel: "",
  });
  useEffect(() => {
    api.get("/addresses/localities").then((r) => setLocs(r.data || []))
      .catch(() => setLocs([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    setStreet(""); setStreets([]);
    if (!sel) return;
    api.get(`/addresses/streets?locality=${encodeURIComponent(sel)}`)
      .then((r) => setStreets(r.data || [])).catch(() => setStreets([]));
  }, [sel]);
  const opts = (k) => (settings && settings[k]) || [];
  const current = locs.find((l) => l.locality === sel);
  const body = () => ({
    locality: sel, wojewodztwo: current?.wojewodztwo || "",
    street, num_from: numFrom, num_to: numTo, shared, skip_existing: true,
  });
  const loadPreview = async () => {
    if (!sel) { toast.error(t("Wybierz miejscowość")); return; }
    setBusy(true);
    try {
      const r = await api.post("/addresses/coverage-bulk-preview", body());
      setExcluded(new Set((r.data.addresses || []).filter((a) => a.exists).map((a) => a.key)));
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Błąd tworzenia zasięgów"));
    } finally { setBusy(false); }
  };
  const toggleAddr = (key) => setExcluded((prev) => {
    const n = new Set(prev);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });
  const confirmSave = async () => {
    setBusy(true);
    try {
      const r = await api.post("/addresses/coverage-bulk", { ...body(), exclude: Array.from(excluded) });
      toast.success(`${t("Utworzono zasięgi:")} ${r.data.created} · ${t("pominięto")}: ${r.data.skipped}`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Błąd tworzenia zasięgów"));
    } finally { setBusy(false); }
  };
  const inp = "w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm outline-none focus:ring-1 focus:ring-teal-500";
  return (
    <div className="fixed inset-0 z-[1400] bg-black/60 flex items-center justify-center p-4" data-testid="coverage-bulk-dialog">
      <div className={`bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg p-5 space-y-3 max-h-[90vh] overflow-y-auto ${preview ? "hidden" : ""}`}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-teal-300">{t("Zasięg dla całej miejscowości")}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="coverage-bulk-close"><X size={18} /></button>
        </div>
        <p className="text-xs text-slate-400">{t("Wybierz miejscowość — utworzymy osobny wpis zasięgu dla każdego adresu z bazy adresowej, z tymi samymi danymi poniżej.")}</p>
        <div>
          <label className="text-xs text-slate-400">{t("Miejscowość")}</label>
          <select value={sel} onChange={(e) => setSel(e.target.value)} className={inp} data-testid="coverage-bulk-locality" disabled={loading}>
            <option value="">{loading ? t("Ładowanie…") : t("— wybierz —")}</option>
            {locs.map((l) => <option key={l.locality + l.wojewodztwo} value={l.locality}>{l.locality} ({l.count})</option>)}
          </select>
        </div>
        {sel && (
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-3">
              <label className="text-xs text-slate-400">{t("Ulica (opcjonalnie)")}</label>
              <select value={street} onChange={(e) => setStreet(e.target.value)} className={inp} data-testid="coverage-bulk-street">
                <option value="">{t("— wszystkie ulice —")}</option>
                {streets.map((s) => <option key={s.street} value={s.street}>{s.street || t("(bez ulicy)")} ({s.count})</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400">{t("Nr od")}</label>
              <input value={numFrom} onChange={(e) => setNumFrom(e.target.value)} className={inp} data-testid="coverage-bulk-numfrom" placeholder="1" />
            </div>
            <div>
              <label className="text-xs text-slate-400">{t("Nr do")}</label>
              <input value={numTo} onChange={(e) => setNumTo(e.target.value)} className={inp} data-testid="coverage-bulk-numto" placeholder="100" />
            </div>
          </div>
        )}
        {[["medium", "Medium transmisyjne", "coverage_media"], ["technologia", "Technologia dostępowa", "coverage_technologies"], ["downlink_mbps", "Maks. Downlink (Mb/s)", "coverage_bandwidths"], ["uplink_mbps", "Maks. Uplink (Mb/s)", "coverage_bandwidths"]].map(([k, label, ok]) => (
          <div key={k}>
            <label className="text-xs text-slate-400">{t(label)}</label>
            <select value={shared[k]} onChange={(e) => setShared((p) => ({ ...p, [k]: e.target.value }))} className={inp} data-testid={`coverage-bulk-${k}`}>
              <option value="">—</option>
              {opts(ok).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        ))}
        <div>
          <label className="text-xs text-slate-400">{t("Rodzaj zasięgu")}</label>
          <select value={shared.rodzaj_zasiegu} onChange={(e) => setShared((p) => ({ ...p, rodzaj_zasiegu: e.target.value }))} className={inp} data-testid="coverage-bulk-rodzaj">
            <option value="rzeczywisty">{t("rzeczywisty")}</option>
            <option value="teoretyczny">{t("teoretyczny")}</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-400">{t("Możliwość dostarczenia usługi na infrastrukturze własnej")}</label>
          <select value={shared.infrastruktura_wlasna} onChange={(e) => setShared((p) => ({ ...p, infrastruktura_wlasna: e.target.value }))} className={inp} data-testid="coverage-bulk-infra">
            <option value="tak">{t("tak")}</option>
            <option value="nie">{t("nie")}</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-400">{t("Przedstawiciel")}</label>
          <input value={shared.przedstawiciel} onChange={(e) => setShared((p) => ({ ...p, przedstawiciel: e.target.value }))} className={inp} data-testid="coverage-bulk-rep" />
        </div>
        {current && <p className="text-xs text-teal-400">{t("Adresów w miejscowości:")} {current.count}</p>}
        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="flex-1 bg-slate-700 hover:bg-slate-600 rounded py-2 text-sm" data-testid="coverage-bulk-cancel">{t("Anuluj")}</button>
          <button onClick={loadPreview} disabled={busy || !sel} className="flex-1 bg-teal-600 hover:bg-teal-500 disabled:opacity-50 rounded py-2 text-sm font-medium flex items-center justify-center gap-2" data-testid="coverage-bulk-submit">
            {busy && <Loader2 size={15} className="animate-spin" />}{t("Podgląd adresów")}
          </button>
        </div>
      </div>

      {preview && (
        <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg p-5 flex flex-col max-h-[90vh]" data-testid="coverage-bulk-preview">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-teal-300">{t("Podgląd adresów")}</h3>
            <button onClick={() => setPreview(null)} className="text-slate-400 hover:text-white" data-testid="coverage-bulk-preview-close"><X size={18} /></button>
          </div>
          <p className="text-xs text-slate-400 mb-2">
            {t("Odznacz adresy, których nie chcesz dodawać. Do utworzenia:")}{" "}
            <b className="text-teal-400">{preview.total - excluded.size}</b> / {preview.total}
          </p>
          <div className="flex gap-2 mb-2 text-xs">
            <button onClick={() => setExcluded(new Set())} className="text-teal-400 hover:underline" data-testid="coverage-bulk-select-all">{t("Zaznacz wszystkie")}</button>
            <span className="text-slate-600">·</span>
            <button onClick={() => setExcluded(new Set(preview.addresses.map((a) => a.key)))} className="text-slate-400 hover:underline" data-testid="coverage-bulk-deselect-all">{t("Odznacz wszystkie")}</button>
          </div>
          <div className="flex-1 overflow-y-auto border border-slate-800 rounded divide-y divide-slate-800">
            {preview.addresses.map((a) => (
              <label key={a.key} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-800/50 cursor-pointer" data-testid={`coverage-bulk-addr-${a.key}`}>
                <input type="checkbox" checked={!excluded.has(a.key)} onChange={() => toggleAddr(a.key)} className="accent-teal-500" />
                <span className="text-slate-300">{a.street || t("(bez ulicy)")} {a.number}</span>
                {a.exists && <span className="ml-auto text-[10px] text-amber-400">{t("już istnieje")}</span>}
              </label>
            ))}
          </div>
          <div className="flex gap-2 pt-3">
            <button onClick={() => setPreview(null)} className="flex-1 bg-slate-700 hover:bg-slate-600 rounded py-2 text-sm" data-testid="coverage-bulk-back">{t("Wstecz")}</button>
            <button onClick={confirmSave} disabled={busy || (preview.total - excluded.size) < 1} className="flex-1 bg-teal-600 hover:bg-teal-500 disabled:opacity-50 rounded py-2 text-sm font-medium flex items-center justify-center gap-2" data-testid="coverage-bulk-confirm">
              {busy && <Loader2 size={15} className="animate-spin" />}{t("Utwórz zasięgi")} ({preview.total - excluded.size})
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CoverageTrashDialog({ onClose, onDone, t }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/coverage/del/trash").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const restore = async (b) => {
    setBusy(b);
    try { const r = await api.post(`/coverage/restore/${b}`); toast.success(`${t("Przywrócono")}: ${r.data.restored}`); load(); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("Błąd przywracania")); }
    finally { setBusy(null); }
  };
  return (
    <div className="fixed inset-0 z-[1400] bg-black/60 flex items-center justify-center p-4" data-testid="coverage-trash-dialog">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg p-5 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-base flex items-center gap-2"><Trash2 size={18} className="text-cyan-400" /> {t("Kosz zasięgu — przywracanie")}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="coverage-trash-close"><X size={18} /></button>
        </div>
        <p className="text-xs text-slate-400 mb-3">{t("Ostatnio usunięte partie zasięgu. Możesz je przywrócić w razie pomyłki.")}</p>
        {rows.length === 0 ? (
          <div className="text-sm text-slate-500 py-4 text-center" data-testid="coverage-trash-empty">{t("Kosz jest pusty")}</div>
        ) : (
          <div className="space-y-2" data-testid="coverage-trash-list">
            {rows.map((r) => (
              <div key={r.batch_id} data-testid={`trash-item-${r.batch_id}`} className="flex items-center gap-2 rounded-md border border-slate-800 bg-slate-800/40 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-200 truncate"><b>{r.count}</b> {t("adresów")} · {r.desc || "—"}</div>
                  <div className="text-[11px] text-slate-500">{String(r.deleted_at || "").slice(0, 16).replace("T", " ")} · {r.by || ""}</div>
                </div>
                <button onClick={() => restore(r.batch_id)} disabled={busy === r.batch_id} data-testid={`trash-restore-${r.batch_id}`}
                  className="bg-cyan-700 hover:bg-cyan-600 disabled:opacity-50 rounded px-2.5 py-1.5 text-xs shrink-0">{busy === r.batch_id ? t("…") : t("Przywróć")}</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ImportTrashDialog({ moduleKey, onClose, onDone, t }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/import-trash").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const restore = async (b) => {
    setBusy(b);
    try { const r = await api.post(`/import-trash/restore/${b}`); toast.success(`${t("Przywrócono")}: ${r.data.restored}`); load(); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("Błąd przywracania")); }
    finally { setBusy(null); }
  };
  return (
    <div className="fixed inset-0 z-[1400] bg-black/60 flex items-center justify-center p-4" data-testid="import-trash-dialog">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg p-5 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-base flex items-center gap-2"><Trash2 size={18} className="text-cyan-400" /> {t("Kosz importu — przywracanie warstw")}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="import-trash-close"><X size={18} /></button>
        </div>
        <p className="text-xs text-slate-400 mb-3">{t("Ostatnio usunięte warstwy importu (wszystkie moduły). Możesz je przywrócić w razie pomyłki.")}</p>
        {rows.length === 0 ? (
          <div className="text-sm text-slate-500 py-4 text-center" data-testid="import-trash-empty">{t("Kosz jest pusty")}</div>
        ) : (
          <div className="space-y-2" data-testid="import-trash-list">
            {rows.map((r) => (
              <div key={r.batch_id} data-testid={`imptrash-item-${r.batch_id}`} className="flex items-center gap-2 rounded-md border border-slate-800 bg-slate-800/40 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-200 truncate"><b>{r.count}</b> {t("obiektów")} · {r.layer || "—"} <span className="text-slate-500">({r.coll})</span></div>
                  <div className="text-[11px] text-slate-500">{String(r.deleted_at || "").slice(0, 16).replace("T", " ")} · {r.by || ""}</div>
                </div>
                <button onClick={() => restore(r.batch_id)} disabled={busy === r.batch_id} data-testid={`imptrash-restore-${r.batch_id}`}
                  className="bg-cyan-700 hover:bg-cyan-600 disabled:opacity-50 rounded px-2.5 py-1.5 text-xs shrink-0">{busy === r.batch_id ? t("…") : t("Przywróć")}</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CoverageBulkDeleteDialog({ onClose, onDone, t }) {
  const [locs, setLocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState("");
  const [streets, setStreets] = useState([]);
  const [street, setStreet] = useState("");
  const [numFrom, setNumFrom] = useState("");
  const [numTo, setNumTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(null);
  useEffect(() => {
    api.get("/coverage/del/localities").then((r) => setLocs(r.data || []))
      .catch(() => setLocs([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    setStreet(""); setStreets([]); setCount(null);
    if (!sel) return;
    api.get(`/coverage/del/streets?locality=${encodeURIComponent(sel)}`)
      .then((r) => setStreets(r.data || [])).catch(() => setStreets([]));
  }, [sel]);
  const body = () => ({ locality: sel, street, num_from: numFrom, num_to: numTo });
  const preview = async () => {
    if (!sel) { toast.error(t("Wybierz miejscowość")); return; }
    setBusy(true);
    try { const r = await api.post("/coverage/bulk-delete-preview", body()); setCount(r.data.total); }
    catch (e) { toast.error(e?.response?.data?.detail || t("Błąd")); }
    finally { setBusy(false); }
  };
  const confirmDelete = async () => {
    if (!window.confirm(`${t("Usunąć z zasięgu")} ${count} ${t("adresów")}? ${t("Tej operacji nie można cofnąć.")}`)) return;
    setBusy(true);
    try {
      const r = await api.post("/coverage/bulk-delete", body());
      toast.success(`${t("Usunięto z zasięgu")}: ${r.data.deleted}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || t("Błąd usuwania")); }
    finally { setBusy(false); }
  };
  const inp = "w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm outline-none focus:ring-1 focus:ring-red-500";
  const cur = locs.find((l) => l.locality === sel);
  return (
    <div className="fixed inset-0 z-[1400] bg-black/60 flex items-center justify-center p-4" data-testid="coverage-del-dialog">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-base flex items-center gap-2"><Trash2 size={18} className="text-red-400" /> {t("Usuń zasięg zbiorczo")}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="coverage-del-close"><X size={18} /></button>
        </div>
        <p className="text-xs text-slate-400 mb-3">{t("Usuń zasięg sieci dla całej miejscowości lub wybranej ulicy (opcjonalnie zakres numerów).")}</p>
        <label className="text-[11px] text-slate-500 uppercase">{t("Miejscowość")}</label>
        <select value={sel} onChange={(e) => setSel(e.target.value)} className={inp} data-testid="coverage-del-locality" disabled={loading}>
          <option value="">{loading ? t("Ładowanie…") : t("— wybierz —")}</option>
          {locs.map((l) => <option key={l.locality} value={l.locality}>{l.locality} ({l.count})</option>)}
        </select>
        {sel && (
          <>
            <label className="text-[11px] text-slate-500 uppercase mt-2 block">{t("Ulica")} ({t("opcjonalnie")})</label>
            <select value={street} onChange={(e) => setStreet(e.target.value)} className={inp} data-testid="coverage-del-street">
              <option value="">{t("cała miejscowość")}</option>
              {streets.map((s) => <option key={s.street} value={s.street}>{s.street || t("(bez ulicy)")} ({s.count})</option>)}
            </select>
            <div className="flex gap-2 mt-2">
              <div className="flex-1">
                <label className="text-[11px] text-slate-500 uppercase">{t("Nr od")}</label>
                <input value={numFrom} onChange={(e) => setNumFrom(e.target.value)} className={inp} data-testid="coverage-del-numfrom" placeholder="1" />
              </div>
              <div className="flex-1">
                <label className="text-[11px] text-slate-500 uppercase">{t("Nr do")}</label>
                <input value={numTo} onChange={(e) => setNumTo(e.target.value)} className={inp} data-testid="coverage-del-numto" placeholder="100" />
              </div>
            </div>
          </>
        )}
        {count !== null && (
          <div className="mt-3 text-sm rounded-md border border-red-500/40 bg-red-500/10 text-red-300 px-3 py-2" data-testid="coverage-del-count">
            {t("Do usunięcia")}: <b>{count}</b> {t("adresów z zasięgu")} {cur ? `(${cur.locality})` : ""}
          </div>
        )}
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 bg-slate-700 hover:bg-slate-600 rounded py-2 text-sm" data-testid="coverage-del-cancel">{t("Anuluj")}</button>
          {count === null ? (
            <button onClick={preview} disabled={busy || !sel} data-testid="coverage-del-preview"
              className="flex-1 bg-red-700 hover:bg-red-600 disabled:opacity-50 rounded py-2 text-sm font-medium flex items-center justify-center gap-2">
              {busy ? <Loader2 size={15} className="animate-spin" /> : null} {t("Sprawdź")}
            </button>
          ) : (
            <button onClick={confirmDelete} disabled={busy || count < 1} data-testid="coverage-del-confirm"
              className="flex-1 bg-red-700 hover:bg-red-600 disabled:opacity-50 rounded py-2 text-sm font-medium flex items-center justify-center gap-2">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} {t("Usuń z zasięgu")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


export default function ModulePanel({ moduleKey, items, slacks = [], onClose, onRefresh, onLocate, onTrace, onFibers, onAddOnMap, onDetails, onTrays, onDrawOnMap, onSplitter, onODF, onOLT, onSwitch, onCabinet, onClosureView, onShowRoute, onEditRoute, onClientAdded, mapCenter, role, side = "left", folders: allFolders = [], onFoldersChanged }) {
  const folders = useMemo(() => allFolders.filter((f) => f.module === moduleKey), [allFolders, moduleKey]);
  const [activeFolder, setActiveFolder] = useState("all");
  const cfg = MODULES[moduleKey];
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [form, setForm] = useState(null); // {initial}
  const [importLayers, setImportLayers] = useState([]);
  const [selLayer, setSelLayer] = useState("");
  const [impTrash, setImpTrash] = useState(false);
  const [trashCount, setTrashCount] = useState(0);
  const canEdit = role !== "odczyt";
  const canDelete = role === "admin" || role === "projektant";
  const [ukeBusy, setUkeBusy] = useState(false);
  const [ukeRodzaj, setUkeRodzaj] = useState("all");
  const [bulk, setBulk] = useState(false);
  const [bulkDel, setBulkDel] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const [showCams, setShowCams] = useState(false);

  const downloadSwitchPassport = async (sw) => {
    try {
      const res = await api.get(`/switches/${sw.id}/passport`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `paszport_switcha_${sw.name || sw.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error(t("Błąd generowania PDF")); }
  };

  const downloadAllSwitchesPassport = async () => {
    try {
      const res = await api.get(`/switches-passport`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "paszport_switchy.pdf";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(t("Paszport switchy gotowy — plik pobrany"));
    } catch { toast.error(t("Błąd generowania PDF")); }
  };

  const downloadNetworkPassport = async () => {
    try {
      const res = await api.get(`/network-passport`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "paszport_sieci.pdf";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(t("Paszport sieci gotowy — plik pobrany"));
    } catch { toast.error(t("Błąd generowania PDF")); }
  };

  const exportUke = async (fmt) => {
    setUkeBusy(true);
    try {
      const rq = ukeRodzaj !== "all" ? `&rodzaj=${ukeRodzaj}` : "";
      const res = await api.get(`/uke/coverage-export?format=${fmt}${rq}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = fmt === "xlsx" ? "zasiegi_uke.xlsx" : "zasiegi_uke.csv";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(t("Eksport UKE gotowy — plik pobrany"));
    } catch { toast.error(t("Błąd eksportu UKE")); }
    finally { setUkeBusy(false); }
  };

  const importUke = async (fileObj) => {
    if (!window.confirm("Zaimportować zasięgi z pliku UKE?\n\nOK = DODAJ do istniejących.\nAnuluj = przerwij (aby zastąpić wszystko, usuń najpierw ręcznie).")) return;
    setUkeBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      fd.append("replace", "false");
      const { data } = await api.post("/uke/coverage-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Zaimportowano ${data.imported} zasięgów z pliku UKE`);
      onRefresh();
    } catch (e) { toast.error("Błąd importu UKE — sprawdź nagłówki kolumn"); }
    finally { setUkeBusy(false); }
  };

  useEffect(() => {
    if (["poles", "wells", "ducts", "microducts", "pipelines", "zones"].includes(moduleKey) && canDelete) {
      api.get(`/import-layers/${moduleKey}`).then(({ data }) => setImportLayers(data)).catch(() => setImportLayers([]));
      api.get(`/import-trash`).then(({ data }) => setTrashCount((data || []).filter((x) => x.coll === moduleKey).length)).catch(() => setTrashCount(0));
    }
  }, [moduleKey, canDelete, items.length]);

  const cols = cfg.fields.slice(0, 3);
  const filtered = useMemo(() => {
    const inFolder = activeFolder === "all" ? items
      : items.filter((it) => (activeFolder === "none" ? !it.folder_id : it.folder_id === activeFolder));
    if (!q) return inFolder;
    const s = q.toLowerCase();
    let out = inFolder.filter((it) => JSON.stringify(it).toLowerCase().includes(s));
    if (sortBy) {
      const relOf = (it) => String(_impVal(it, ["relacja", "relation"]) || "").toLowerCase();
      const lenOf = (it) => { const n = parseFloat(String(_impVal(it, ["długość", "dlugosc", "length"]) ?? "").replace(",", ".")); return isNaN(n) ? -Infinity : n; };
      out = [...out].sort((a, b) => {
        if (sortBy === "relacja") return relOf(a).localeCompare(relOf(b), "pl");
        if (sortBy === "dlugosc_desc") return lenOf(b) - lenOf(a);
        if (sortBy === "dlugosc_asc") return lenOf(a) - lenOf(b);
        return 0;
      });
    }
    return out;
  }, [items, q, sortBy, activeFolder]);

  const del = async (it) => {
    if (!window.confirm(`Usunąć ${cfg.singular} ${it[cfg.display] || ""}?`)) return;
    try {
      await api.delete(`/${moduleKey}/${it.id}`);
      toast.success(t("Usunięto"));
      onRefresh();
    } catch (e) { toast.error(t("Błąd usuwania")); }
  };

  const delAll = async () => {
    if (!window.confirm(`Usunąć WSZYSTKIE rekordy (${items.length}) w module „${cfg.label}"? Tej operacji nie można cofnąć.`)) return;
    try {
      const { data } = await api.delete(`/bulk/${moduleKey}`);
      toast.success(`${t("Usunięto")} ${data.deleted} ${t("rekordów")}`);
      onRefresh();
    } catch (e) { toast.error(t("Błąd masowego usuwania")); }
  };

  const delLayer = async () => {
    if (!selLayer) return;
    const info = importLayers.find((l) => l.layer === selLayer);
    if (!window.confirm(`Usunąć obiekty z warstwy „${info?.label || selLayer}" (${info?.count || 0}) w module „${t(cfg.label)}"?\n\nTrafią do Kosza importu — będzie można je przywrócić.`)) return;
    try {
      const { data } = await api.post(`/import-layers/${moduleKey}/delete`, { import_layer: selLayer });
      toast.success(`Usunięto ${data.deleted} obiektów — możesz cofnąć w Koszu importu`);
      setSelLayer("");
      onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || t("Błąd usuwania warstwy")); }
  };

  return (
    <div className="absolute inset-0 bg-slate-900 flex flex-col" data-testid={`module-panel-${moduleKey}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
        <div className="flex items-center gap-2">
          <cfg.icon size={18} className="text-cyan-400" />
          <h2 className="font-heading font-bold text-base">{t(cfg.label)}</h2>
          <span className="text-xs text-slate-500 bg-slate-800 rounded px-1.5 py-0.5 font-mono">{items.length}</span>
        </div>
        <button onClick={onClose} data-testid="module-panel-close" className="text-slate-400 hover:text-white"><X size={18} /></button>
      </div>

      <div className="px-4 py-2.5 border-b border-slate-800 flex gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-2.5 top-2.5 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Filtruj…")}
            data-testid="module-filter-input"
            className="w-full bg-slate-800 border border-slate-700 rounded pl-8 pr-2 py-1.5 text-sm outline-none focus:ring-1 focus:ring-cyan-500" />
        </div>
        {["poles", "wells", "ducts", "microducts", "pipelines", "zones"].includes(moduleKey) && (
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="module-sort-select"
            title={t("Sortuj wg pól z importu")}
            className="bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-cyan-500 shrink-0">
            <option value="">{t("Sortuj: domyślnie")}</option>
            <option value="relacja">{t("Relacja (A–Z)")}</option>
            <option value="dlugosc_desc">{t("Długość ↓")}</option>
            <option value="dlugosc_asc">{t("Długość ↑")}</option>
          </select>
        )}
        {canEdit && (
          <button onClick={() => setForm({ initial: null })} data-testid="module-add-button"
            className="bg-blue-600 hover:bg-blue-500 rounded px-3 flex items-center gap-1 text-sm font-medium">
            <Plus size={15} /> {t("Dodaj")}
          </button>
        )}
        {canEdit && moduleKey === "coverage" && (
          <button onClick={() => setBulk(true)} data-testid="coverage-bulk-button"
            title={t("Dodaj zasięg dla całej miejscowości (z bazy adresowej)")}
            className="bg-teal-700 hover:bg-teal-600 rounded px-3 flex items-center gap-1 text-sm font-medium">
            <Plus size={15} /> {t("Miejscowość")}
          </button>
        )}
        {canDelete && moduleKey === "coverage" && (
          <button onClick={() => setBulkDel(true)} data-testid="coverage-del-button"
            title={t("Usuń zasięg dla całej miejscowości lub ulicy")}
            className="bg-red-800 hover:bg-red-700 rounded px-3 flex items-center gap-1 text-sm font-medium whitespace-nowrap">
            <Trash2 size={15} /> <span className="hidden sm:inline">{t("Usuń zasięg")}</span>
          </button>
        )}
        {moduleKey === "coverage" && (
          <button onClick={() => setShowTrash(true)} data-testid="coverage-trash-button"
            title={t("Kosz — przywróć usunięty zasięg")}
            className="bg-slate-700 hover:bg-slate-600 rounded px-3 flex items-center gap-1 text-sm whitespace-nowrap">
            <Trash2 size={15} /> <span className="hidden sm:inline">{t("Kosz")}</span>
          </button>
        )}
        {moduleKey === "odf" && (
          <button onClick={() => setShowCams(true)} data-testid="odf-camera-wall-button"
            title={t("Podgląd kamer wszystkich szaf na jednej tablicy")}
            className="bg-indigo-700 hover:bg-indigo-600 rounded px-2.5 flex items-center text-sm">
            <Camera size={15} />
          </button>
        )}
        {canEdit && cfg.geo === "point" && (
          <button onClick={onAddOnMap} data-testid="module-add-on-map" title={t("Dodaj klikając na mapie")}
            className="bg-cyan-700 hover:bg-cyan-600 rounded px-2.5 flex items-center text-sm">
            <MapPin size={15} />
          </button>
        )}
        {canEdit && cfg.geo === "line" && (
          <button onClick={() => onDrawOnMap(false)} data-testid="module-draw-on-map" title={t("Rysuj trasę na mapie (przyciąganie do słupów/muf)")}
            className="bg-cyan-700 hover:bg-cyan-600 rounded px-2.5 flex items-center text-sm">
            <Spline size={15} />
          </button>
        )}
        {canEdit && cfg.geo === "line" && (
          <button onClick={() => onDrawOnMap(true)} data-testid="module-draw-nosnap" title={t("Rysuj trasę BEZ przyciągania (dowolne punkty)")}
            className="bg-amber-700 hover:bg-amber-600 rounded px-2.5 flex items-center gap-1 text-sm whitespace-nowrap">
            <Unlink size={15} /> <span className="hidden sm:inline text-xs">{t("bez przyciągania")}</span>
          </button>
        )}
        {canDelete && items.length > 0 && (
          <button onClick={delAll} data-testid="module-bulk-delete"
            title={`${t("Usuń wszystkie")} (${items.length})`}
            className="bg-red-700 hover:bg-red-600 rounded px-2.5 flex items-center text-sm">
            <Trash2 size={15} />
          </button>
        )}
        {moduleKey === "odf" && (
          <button onClick={downloadNetworkPassport} data-testid="odf-network-passport" title={t("Paszport całej sieci (PDF)")}
            className="bg-slate-700 hover:bg-slate-600 rounded px-2.5 flex items-center gap-1 text-sm">
            <Download size={15} /> <span className="hidden sm:inline text-xs">{t("Paszport sieci")}</span>
          </button>
        )}
        {moduleKey === "switches" && (
          <button onClick={downloadAllSwitchesPassport} data-testid="switches-passport-all" title={t("Paszport wszystkich switchy (PDF)")}
            className="bg-slate-700 hover:bg-slate-600 rounded px-2.5 flex items-center gap-1 text-sm">
            <Download size={15} /> <span className="hidden sm:inline text-xs">{t("Paszport switchy")}</span>
          </button>
        )}
      </div>

      {moduleKey === "coverage" && (
        <div className="px-4 py-2 border-b border-slate-800 flex flex-wrap gap-2 items-center bg-slate-900/60" data-testid="coverage-uke-bar">
          <span className="text-xs text-slate-400 shrink-0">{t("Format UKE/PIT:")}</span>
          <select value={ukeRodzaj} onChange={(e) => setUkeRodzaj(e.target.value)} data-testid="uke-rodzaj"
            className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-cyan-500">
            <option value="all">{t("Wszystkie")}</option>
            <option value="rzeczywisty">{t("Tylko rzeczywiste")}</option>
            <option value="teoretyczny">{t("Tylko teoretyczne")}</option>
          </select>
          <button onClick={() => exportUke("csv")} disabled={ukeBusy} data-testid="uke-export-csv"
            className="bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded px-2.5 py-1 flex items-center gap-1 text-xs disabled:opacity-50">
            {ukeBusy ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} {t("Eksport CSV")}
          </button>
          <button onClick={() => exportUke("xlsx")} disabled={ukeBusy} data-testid="uke-export-xlsx"
            className="bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded px-2.5 py-1 flex items-center gap-1 text-xs disabled:opacity-50">
            {ukeBusy ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} {t("Eksport XLSX")}
          </button>
          {canDelete && (
            <label data-testid="uke-import" title={t("Import zasięgów z pliku UKE (CSV/XLSX)")}
              className="bg-cyan-800/60 hover:bg-cyan-700/60 border border-cyan-700 rounded px-2.5 py-1 flex items-center gap-1 text-xs cursor-pointer">
              <Upload size={12} /> {t("Import UKE")}
              <input type="file" accept=".csv,.xlsx" className="hidden" data-testid="uke-import-input"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) importUke(f); e.target.value = ""; }} />
            </label>
          )}
        </div>
      )}

      {["poles", "wells", "ducts", "microducts", "pipelines", "zones"].includes(moduleKey) && canDelete && (importLayers.length > 0 || trashCount > 0) && (
        <div className="px-4 py-2 border-b border-slate-800 flex gap-2 items-center bg-slate-900/60">
          {importLayers.length > 0 ? (
            <>
              <span className="text-xs text-slate-400 shrink-0">Usuń wg warstwy importu:</span>
              <select value={selLayer} onChange={(e) => setSelLayer(e.target.value)} data-testid="pole-layer-select"
                className="flex-1 min-w-0 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-cyan-500">
                <option value="">— wybierz warstwę —</option>
                {importLayers.map((l) => (
                  <option key={l.layer} value={l.layer}>{l.label} ({l.count})</option>
                ))}
              </select>
              <button onClick={delLayer} disabled={!selLayer} data-testid="pole-layer-delete"
                className="bg-red-700 hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed rounded px-2.5 py-1 flex items-center gap-1 text-xs">
                <Trash2 size={13} /> Usuń
              </button>
            </>
          ) : (
            <span className="text-xs text-slate-400 flex-1">Brak aktywnych warstw importu.</span>
          )}
          <button onClick={() => setImpTrash(true)} data-testid="import-trash-open" title={t("Kosz importu — przywróć usuniętą warstwę")}
            className="bg-slate-700 hover:bg-slate-600 rounded px-2.5 py-1 flex items-center gap-1 text-xs shrink-0">
            <Trash2 size={13} /> {t("Kosz")}{trashCount > 0 ? ` (${trashCount})` : ""}
          </button>
        </div>
      )}
      {impTrash && <ImportTrashDialog moduleKey={moduleKey} onClose={() => setImpTrash(false)} onDone={onRefresh} t={t} />}

      <FolderBar moduleKey={moduleKey} folders={folders} items={items} active={activeFolder} setActive={setActiveFolder}
        onChanged={() => { onFoldersChanged && onFoldersChanged(); onRefresh(); }} canEdit={canEdit} canDelete={canDelete} t={t} />

      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 && (
          <div className="text-center text-slate-500 text-sm py-10">{t("Brak rekordów")}</div>
        )}
        {filtered.map((it) => (
          <div key={it.id} className="border-b border-slate-800 px-4 py-2.5 hover:bg-slate-800/60 group" data-testid={`row-${moduleKey}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex items-start gap-2">
                {cfg.catalog && it.image_file_id && (
                  <img src={`${API}/files/${it.image_file_id}?auth=${encodeURIComponent(dlToken())}`} alt=""
                    data-testid={`catalog-thumb-${it.id}`}
                    className="w-10 h-10 rounded object-cover border border-slate-700 shrink-0" />
                )}
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{cfg.displayFn ? cfg.displayFn(it) : (it[cfg.display] || it.name || "—")}</div>
                  <div className="text-xs text-slate-400 mt-0.5 flex flex-wrap gap-x-3">
                    {cols.slice(1).map((c) => it[c.name] ? (
                      <span key={c.name} className="truncate">{t(c.label)}: <span className="text-slate-300">{it[c.name]}</span></span>
                    ) : null)}
                  </div>
                  {it.import_layer && (() => {
                    const rel = _impVal(it, ["relacja", "relation"]);
                    const dl = _impVal(it, ["długość", "dlugosc", "length"]);
                    if (!rel && !dl) return null;
                    return (
                      <div className="text-[11px] mt-0.5 text-slate-400 flex flex-wrap gap-x-3" data-testid={`import-cols-${it.id}`}>
                        {rel ? <span className="truncate max-w-[280px]">{t("Relacja")}: <span className="text-slate-300">{String(rel)}</span></span> : null}
                        {dl ? <span>{t("Długość")}: <span className="text-slate-300">{String(dl)}</span></span> : null}
                      </div>
                    );
                  })()}
                  {moduleKey === "cables" && (() => { const o = cableOptical(it, slacks); return (
                    <div className="text-[11px] mt-0.5 text-slate-400" data-testid={`cable-optical-${it.id}`}>
                      {t("Trasa")}: <span className="text-slate-300">{o.route} m</span>
                      {" · "}{t("Optyczna")}: <span className="text-cyan-300 font-medium">{o.optical} m</span>
                      {o.slack > 0 && <span className="text-rose-300"> (+{o.slack} m {t("zapasu")})</span>}
                    </div>
                  ); })()}
                </div>
              </div>
              <div className="flex items-center gap-1 opacity-70 group-hover:opacity-100 shrink-0">
                <button onClick={() => onDetails(it)} title={t("Dokumentacja / QR")} data-testid={`details-btn-${it.id}`}
                  className="p-1.5 rounded hover:bg-cyan-600/30 text-cyan-400"><FolderOpen size={14} /></button>
                {moduleKey === "clients" && (
                  <button onClick={() => onTrace(it)} title={t("Śledź sygnał")} data-testid={`trace-btn-${it.id}`}
                    className="p-1.5 rounded hover:bg-cyan-600/30 text-cyan-400"><Zap size={14} /></button>
                )}
                {moduleKey === "cables" && (
                  <button onClick={() => onFibers(it)} title={t("Włókna")} data-testid={`fibers-btn-${it.id}`}
                    className="p-1.5 rounded hover:bg-blue-600/30 text-blue-400"><Layers3 size={14} /></button>
                )}
                {cfg?.geo === "line" && onShowRoute && it.geometry?.length > 1 && (
                  <button onClick={() => onShowRoute(it)} title={t("Pokaż trasę na mapie (miga)")} data-testid={`show-route-btn-${it.id}`}
                    className="p-1.5 rounded hover:bg-red-600/30 text-red-400"><MapPin size={14} /></button>
                )}
                {onEditRoute && it.geometry?.length > 1 && (
                  <button onClick={() => onEditRoute(it)} title={t("Edytuj trasę na mapie")} data-testid={`edit-route-btn-${it.id}`}
                    className="p-1.5 rounded hover:bg-amber-600/30 text-amber-400"><Waypoints size={14} /></button>
                )}
                {moduleKey === "closures" && onClosureView && (
                  <button onClick={() => onClosureView(it)} title={t("Podgląd mufy")} data-testid={`closure-btn-${it.id}`}
                    className="p-1.5 rounded hover:bg-purple-600/30 text-purple-400"><Boxes size={14} /></button>
                )}
                {moduleKey === "closures" && (
                  <button onClick={() => onTrays(it)} title={t("Widok tacek")} data-testid={`trays-btn-${it.id}`}
                    className="p-1.5 rounded hover:bg-purple-600/30 text-purple-400"><Grid3x3 size={14} /></button>
                )}
                {moduleKey === "splitters" && (
                  <button onClick={() => onSplitter(it)} title={t("Schemat splittera")} data-testid={`splitter-btn-${it.id}`}
                    className="p-1.5 rounded hover:bg-orange-600/30 text-orange-400"><Split size={14} /></button>
                )}
                {moduleKey === "patchpanels" && (
                  <button onClick={() => onODF(it)} title={t("Podgląd przełącznicy")} data-testid={`odf-btn-${it.id}`}
                    className="p-1.5 rounded hover:bg-emerald-600/30 text-emerald-400"><Rows3 size={14} /></button>
                )}
                {moduleKey === "odf" && (
                  <button onClick={() => onCabinet(it)} title={t("Widok szafy (rack)")} data-testid={`cabinet-btn-${it.id}`}
                    className="p-1.5 rounded hover:bg-green-600/30 text-green-400"><Server size={14} /></button>
                )}
                {moduleKey === "olt" && onOLT && (
                  <button onClick={() => onOLT(it)} title={t("Podgląd OLT (porty)")} data-testid={`olt-btn-${it.id}`}
                    className="p-1.5 rounded hover:bg-blue-600/30 text-blue-400"><Server size={14} /></button>
                )}
                {moduleKey === "switches" && onSwitch && (
                  <button onClick={() => onSwitch(it)} title="Podgląd switcha (porty)" data-testid={`switch-btn-${it.id}`}
                    className="p-1.5 rounded hover:bg-amber-600/30 text-amber-400"><Server size={14} /></button>
                )}
                {moduleKey === "switches" && (
                  <button onClick={() => downloadSwitchPassport(it)} title="Paszport switcha (PDF)" data-testid={`switch-passport-${it.id}`}
                    className="p-1.5 rounded hover:bg-amber-600/30 text-amber-300"><FileText size={14} /></button>
                )}
                {canEdit && <FolderSelect moduleKey={moduleKey} item={it} folders={folders} onChanged={onRefresh} t={t} />}
                {(it.lat != null || it.geometry?.length > 1) && (
                  <button onClick={() => onLocate(it)} title={t("Pokaż na mapie")} data-testid={`locate-btn-${it.id}`}
                    className="p-1.5 rounded hover:bg-slate-700 text-slate-300"><MapPin size={14} /></button>
                )}
                {canEdit && (
                  <button onClick={() => setForm({ initial: it })} title={t("Edytuj")} data-testid={`edit-btn-${it.id}`}
                    className="p-1.5 rounded hover:bg-slate-700 text-slate-300"><Pencil size={14} /></button>
                )}
                {canDelete && (
                  <button onClick={() => del(it)} title={t("Usuń")} data-testid={`delete-btn-${it.id}`}
                    className="p-1.5 rounded hover:bg-red-600/30 text-red-400"><Trash2 size={14} /></button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {form && (
        <RecordForm moduleKey={moduleKey} initial={form.initial} mapCenter={mapCenter}
          onClose={() => setForm(null)}
          onSaved={async (saved) => {
            const wasNew = !form.initial?.id; setForm(null);
            if (wasNew && saved?.id && folders.some((f) => f.id === activeFolder)) {
              await api.post("/folders/assign", { module: moduleKey, ids: [saved.id], folder_id: activeFolder }).catch(() => {});
            }
            onRefresh(); if (moduleKey === "clients" && wasNew && onClientAdded) onClientAdded(saved);
          }} />
      )}
      {bulk && (
        <CoverageBulkDialog t={t} onClose={() => setBulk(false)}
          onDone={() => { setBulk(false); onRefresh && onRefresh(); }} />
      )}
      {bulkDel && (
        <CoverageBulkDeleteDialog t={t} onClose={() => setBulkDel(false)}
          onDone={() => { setBulkDel(false); onRefresh && onRefresh(); }} />
      )}
      {showTrash && (
        <CoverageTrashDialog t={t} onClose={() => setShowTrash(false)}
          onDone={() => { onRefresh && onRefresh(); }} />
      )}
      {showCams && <CameraWall items={items} onClose={() => setShowCams(false)} />}
    </div>
  );
}
